<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import { z } from 'zod'

import { BtnOrange } from '~/components/ui/Buttons'
import VSelect from '~/components/ui/Inputs/VSelect.vue'
import VTextInput from '~/components/ui/Inputs/VText.vue'
import VText from '~/components/ui/VText.vue'
import VTooltip from '~/components/ui/VTooltip.vue'

import { useFieldValidation } from '~/composables/useFieldValidation'
import {
  getLanguageFromTagCountryName,
  useLanguage,
} from '~/composables/useLanguage'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useSortTagsByCountry } from '~/composables/useTagHelpers'

import { useSnackbarStore } from '~/stores/snackbar'
import { useTagStore } from '~/stores/tag'
import { useUserInfluencerStore } from '~/stores/userInfluencer'
import { useUserInfluencerTagsStore } from '~/stores/userInfluencerTags'

import { provideCreateInfluencer } from '~/api-core/Curators/CreateInfluencer'

import type Tag from '~/entities/tag'
import type TagType from '~/entities/tagType'
import type { UserState } from '~/stores/user'
import type { UserInfluencerState } from '~/stores/userInfluencer'

type TagWithI18N = Omit<Tag, 'toJSON'> & {
  i18n: string
}
type Props = {
  modelValue: UserInfluencerState | null
  user: UserState
}
type Emits = {
  'update:modelValue': [tags: UserInfluencerState]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const { coreFetch } = useProvideCoreFetch()
const { setCurrentLanguage } = useLanguage()
const { sortTagsByCountry } = useSortTagsByCountry()

const tagStore = useTagStore()
const { GET_TAGS_FROM_IDS, GET_TAG_TYPE_FROM_NAME } = tagStore
const userInfluencerTagsStore = useUserInfluencerTagsStore()
const { COUNTRY: INFLUENCER_COUNTRY, identity: INFLUENCER_IDENTITY_TAGS } =
  storeToRefs(userInfluencerTagsStore)
const {
  UPDATE_FROM_PATCH: UPDATE_TAGS_FROM_PATCH,
  SET_TARGET: SET_TAGS_FROM_TARGET,
} = userInfluencerTagsStore
const { SET: SET_INFLUENCER } = useUserInfluencerStore()

const TAG_TYPE_INFLUENCER_KIND = computed<TagType | undefined>(() =>
  GET_TAG_TYPE_FROM_NAME('influencer_kind'),
)
const TAG_TYPE_COUNTRY = computed<TagType | undefined>(() =>
  GET_TAG_TYPE_FROM_NAME('country'),
)
const AVAILABLE_COUNTRIES = computed<Tag[]>(() =>
  [...GET_TAGS_FROM_IDS(TAG_TYPE_COUNTRY.value?.tag_ids ?? [])]
    .filter(({ name }) => !name.includes('_'))
    .sort(sortTagsByCountry),
)
const AVAILABLE_KINDS = computed<TagWithI18N[]>(() => {
  if (!TAG_TYPE_COUNTRY.value) return []

  const tags = GET_TAGS_FROM_IDS(TAG_TYPE_INFLUENCER_KIND.value?.tag_ids ?? [])

  return tags
    .filter((tag) => tag.name.toLowerCase() !== 'springboard')
    .sort((valA, valB) => valA.name.localeCompare(valB.name))
    .map((tag) => {
      return {
        ...tag,
        i18n: t(`tags.influencer_kind.${tag.name}`, { count: 1 }),
      }
    })
})
const INFLUENCER_KIND_ID = computed<number>(
  () => INFLUENCER_IDENTITY_TAGS.value.influencer_kind[0] || 0,
)
const INFLUENCER_KIND = computed<TagWithI18N | undefined>(() =>
  AVAILABLE_KINDS.value.find((e) => e.id === INFLUENCER_KIND_ID.value),
)
const { CREATE: CREATE_SNACKBAR } = useSnackbarStore()

const { validate: validateForm } = useForm()
const { value: entityFieldValue, errorMessage: entityFieldErrorMessage } =
  useFieldValidation('influencerEntity', z.string().min(1), {
    initialValue: '',
  })
const {
  value: kindFieldValue,
  errorMessage: kindFieldErrorMessage,
  handleChange: kindFieldValidateOnChange,
} = useFieldValidation(
  'influencerKind',
  z.number().refine(() => !!INFLUENCER_KIND_ID.value),
  { initialValue: INFLUENCER_KIND_ID.value, validateOnValueUpdate: true },
)
watch(INFLUENCER_KIND_ID, (value) => (kindFieldValue.value = value))

const {
  value: countryFieldValue,
  errorMessage: countryFieldErrorMessage,
  handleChange: countryFieldValidateOnChange,
} = useFieldValidation(
  'influencerCountry',
  z.number().refine(() => !!INFLUENCER_COUNTRY.value),
  { initialValue: INFLUENCER_COUNTRY.value, validateOnValueUpdate: true },
)
watch(INFLUENCER_COUNTRY, (value) => (countryFieldValue.value = value))

const loading = ref(false)

const kindIsPlaylist = computed<boolean>(
  () => INFLUENCER_KIND.value?.name.match(/(playlist)/gi) !== null,
)
const kindIsChannel = computed<boolean>(
  () => INFLUENCER_KIND.value?.name.match(/(channel|chaîne)/gi) !== null,
)

function transformCountryLabelInSelect(string: string): string {
  return t(`tags.country.${string}`)
}

async function createInfluencer(): Promise<void> {
  if (loading.value) return

  const { valid } = await validateForm()
  if (!valid) return

  loading.value = true
  try {
    const createCoreInfluencer = provideCreateInfluencer(coreFetch)
    const influencer = await createCoreInfluencer(props.user.id, {
      entity: entityFieldValue.value,
      id: undefined,
      slug: undefined,
      media_links: undefined,
    })
    SET_INFLUENCER(influencer)

    const tags = await UPDATE_TAGS_FROM_PATCH({
      patch: {
        identity: {
          country: [INFLUENCER_COUNTRY.value],
          influencer_kind: [INFLUENCER_KIND_ID.value],
        },
      },
      influencer_id: influencer.id,
    })

    await nextTick()
    const countries = GET_TAGS_FROM_IDS([INFLUENCER_COUNTRY.value])

    if (isDefined(countries[0]))
      setCurrentLanguage(getLanguageFromTagCountryName(countries[0].name))

    emit('update:modelValue', {
      ...influencer,
      tags,
      authorization_reason: '',
      authorization_status: 'pending',
      has_sendtrack_picture: false,
      decisions: [],
    })
  } catch (_) {
    CREATE_SNACKBAR({
      props: {
        icon: 'fas fa-triangle-exclamation',
        text: t('error.dualSignup'),
      },
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="influencerCreateBaseWrapper tw-mt-10">
    <div class="title">
      {{ $t('influencer.create.base00') }} {{ user.first_name }}
    </div>
    <div class="subtitle">{{ $t('influencer.create.base01') }}</div>
    <div class="tw-relative tw-grid tw-w-full tw-grid-cols-1 tw-gap-8">
      <VSelect
        data-test-id="curatorSignupProTypeSelect"
        :p-validity="!kindFieldErrorMessage"
        :model-value="INFLUENCER_KIND_ID"
        :available="AVAILABLE_KINDS"
        value-key="id"
        label-key="i18n"
        :label="$t('influencer.create.base02')"
        :placeholder="$t('influencer.create.base03')"
        @change="kindFieldValidateOnChange($event, true)"
        @update:model-value="
          SET_TAGS_FROM_TARGET({
            target: 'identity',
            patch: { influencer_kind: [$event] },
          })
        "
      />
      <VTooltip>
        <template #tooltipText>{{ $t('influencer.create.base06') }}</template>
        <template #default>
          <VTextInput
            v-model="entityFieldValue"
            data-test-id="curatorSignupPlaylistNameInput"
            :p-validity="!entityFieldErrorMessage"
            :label="
              kindIsPlaylist
                ? $t('influencer.create.base11')
                : kindIsChannel
                  ? $t('influencer.create.base12')
                  : $t('influencer.create.base05')
            "
            :placeholder="
              kindIsPlaylist
                ? $t('influencer.create.base11')
                : kindIsChannel
                  ? $t('influencer.create.base12')
                  : $t('influencer.create.base12')
            "
            @enter="createInfluencer"
          >
            <template #err-message>{{ entityFieldErrorMessage }}</template>
          </VTextInput>
        </template>
      </VTooltip>
      <VSelect
        data-test-id="curatorSignupCountrySelect"
        :p-validity="!countryFieldErrorMessage"
        :model-value="INFLUENCER_COUNTRY"
        :available="AVAILABLE_COUNTRIES"
        value-key="id"
        label-key="name"
        :transform-label-fn="transformCountryLabelInSelect"
        :label="$t('influencer.create.base07')"
        :placeholder="$t('influencer.create.base08')"
        @change="countryFieldValidateOnChange($event, true)"
        @update:model-value="
          SET_TAGS_FROM_TARGET({
            target: 'identity',
            patch: { country: [$event] },
          })
        "
      />
      <div class="tw-w-full tw-space-y-1 tw-pt-8">
        <BtnOrange
          data-test-id="curatorSignupNextCTA"
          :loading="loading"
          class="!tw-w-full"
          @click="createInfluencer"
        >
          {{ $t('influencer.create.base09') }}
        </BtnOrange>
        <VText
          cfg="sans/13/medium"
          color="gray-500"
          class="tw-block tw-w-full tw-text-center"
        >
          {{ $t('influencer.create.base04') }}
        </VText>
      </div>
    </div>
  </div>
</template>

<style scoped>
.influencerCreateBaseWrapper {
  @apply tw-relative tw-w-full;
}

.title {
  @apply tw-mx-auto tw-mb-1 tw-mt-0 tw-text-center tw-font-sans tw-text-3xl tw-font-bold tw-text-black;
}

.subtitle {
  @apply tw-mx-auto tw-mb-16 tw-mt-0 tw-w-full tw-max-w-lg tw-text-center tw-text-base;
  line-height: 1.5;
}

.formWrapper .finalBtn {
  padding-top: 32px;
}

.absoluteAnnotation {
  position: absolute;
  left: calc(100% + 16px);
  max-width: 195px;
  width: 100%;
  text-align: left;
  color: var(--color-masala-grey-h);
  font-size: var(--font-size-micro);
  bottom: 0px;
  min-height: 40px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.formWrapper .questionTooltip {
  background-color: var(--color-pinkish-grey);
  width: 20px;
  height: 20px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  color: var(--color-hint-of-red);
  cursor: help;
}

.limitTooltip {
  max-width: 242px;
}
</style>
<style scoped>
@media screen and (max-width: 720px) {
  .absoluteAnnotation {
    display: block;
    position: relative;
    left: 0;
    width: 100%;
    max-width: unset;
  }

  .formWrapper .questionTooltip {
    width: 35px;
    height: 35px;
    border-radius: 45px;
  }
}
</style>
